// src/pages/TrackCreation/Step1.tsx
import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "../../../../../../components/ui/Button";
import { RouteParamsWithLanguage, StoryPhotosProps } from "../../../../../../types/screensTypes";
import ImageList from "../../../../../../components/images/ImageList";
import { saveStory, getStoryById } from "../../../../../../api/museum";
import { FileInput } from "../../../../../../components/common/FileInput";
import TitleSubtitle from "../../../../../../components/common/TitleSubtitle";
import { useTrackCreation } from "../../../../../../context/TrackCreationContext";
import { uploadFileToS3Complete } from "../../../../../../api/s3";
import { IMAGE_FORMAT, IMAGE_MAX_SIZE_200_KB } from "../../../../../../utils/image";
import ImageSpecsBadges from "../../../../../../components/common/ImageSpecsBadges";
import { useImageValidation } from "../../../../../../hooks/useImageValidation";
import stepStyles from "../../../createStep.module.css";

const StoryPhotos: React.FC<StoryPhotosProps> = ({ navigateToNext }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    // Utility function to extract the story ID from the URL pathname.
    const extractStoryId = (): string | null => {
        const match = location.pathname.match(/story-[^/]+\/([^/]+)/);
        return match ? match[1] : null;
    };

    const { storyId, planId, language } = useParams<RouteParamsWithLanguage>();
    const [pictures, setPictures] = useState<string[]>([]);
    const { trackData } = useTrackCreation();
    const imageValidation = useImageValidation({
        type: "artwork",
        imageUrl: pictures[pictures.length - 1] || undefined,
    });

    const handleFileSelected = async (files: File | File[]) => {
        const file = Array.isArray(files) ? files[0] : files; // Toujours prendre un seul fichier

        if (file) {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("No authentication token found");
                return;
            }

            await imageValidation.validateImage(file);

            uploadFileToS3Complete(
                {
                    file,
                    isPublic: true, // Public files for general uploads
                    isFolder: false, // No folder structure for general uploads
                },
                token,
            )
                .then((result: string) => {
                    setPictures((prevPictures) => [...prevPictures, result]);
                })
                .catch((error) => {
                    console.log(error);
                });
        }
    };

    const saveStoryAPI = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;

        if (!token || !trackId || !storyId || !planId || !language) {
            return;
        }

        await saveStory({
            token,
            trackId,
            clickableZoneId: storyId,
            subplanId: planId,
            language,
            pictures,
        });

        navigate(location.pathname.replace(/\/story-[^/]+\/.+$/, "/museum-stories"), {
            state: { action: extractStoryId() },
        });
    };

    const getStoryAPI = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!token || !trackId || !storyId || !language) {
            return;
        }
        const storyData = await getStoryById({
            token,
            trackId,
            clickableZoneId: storyId,
            language,
        });
        if (storyData) {
            const picturesData = storyData.pictures;
            if (picturesData && picturesData.length > 0) {
                const picturesAPI = picturesData.map((picture: { url: string }) => picture.url);
                setPictures(picturesAPI);
            }
        }
    };

    useEffect(() => {
        if (storyId && storyId !== "") {
            getStoryAPI();
        }
    }, [storyId, language]);

    return (
        <div>
            <TitleSubtitle title="Story photos" subtitle={""} layout="step" />

            <div className={stepStyles.stepDescription} style={{ marginTop: "30px" }}>
                <h2>{t("requirements")}</h2>
                <ImageSpecsBadges spec="artwork" warnings={imageValidation.warnings} />
                <ul className="no-dots">
                    <li>
                        <b>X</b> {t("noPhotographerWatermarksLogosOrReadableLicensePlates")}
                    </li>
                </ul>
            </div>

            <div className={stepStyles.stepDescription} style={{ marginTop: "30px" }}>
                <div className={stepStyles.fileUpload}>
                    <h2>{t("uploadYourPhotos")}</h2>
                    <p>{t("dragYourPhotosIntoTheAreaBelowOrSelectUploadPhotos")}</p>
                    <FileInput
                        onFileSelected={handleFileSelected}
                        showButton={true}
                        acceptedFileTypes={IMAGE_FORMAT}
                        maxSize={IMAGE_MAX_SIZE_200_KB}
                        maxSizeLabel={t("imageMaxSize200kb")}
                    />
                </div>
            </div>

            <div className="photo-gallery">
                <ImageList pictures={pictures} setPictures={setPictures} />
            </div>

            <div className={stepStyles.stepButton}>
                <Button
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                    label={t("continue")}
                    onClick={async () => {
                        await saveStoryAPI();
                    }}
                />
            </div>
        </div>
    );
};

export default StoryPhotos;
